// Página de inicio: KPIs con count-up, recordatorios de hoy y accesos rápidos.
import type { JSX } from "react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  CalendarCheck, Clock, FileText, Handshake, type LucideIcon,
  Target, TrendingUp,
} from "lucide-react";
import { api } from "../lib/api.ts";
import { useMe } from "../lib/auth.ts";
import type { Row } from "../lib/resource.ts";
import { formatMoney } from "../lib/format.ts";
import { Card, Loading } from "../components/ui.tsx";
import { ViewPortfolioButton } from "../components/PortfolioButton.tsx";

function useCount(path: string) {
  return useQuery<{ rows: Row[]; total?: number }>({ queryKey: ["list", path], queryFn: () => api.get(`/${path}`) });
}

/** Anima un número de 0 → target (ease-out) cuando `active` pasa a true. */
function useCountUp(target: number, active: boolean, duration = 800): number {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!active) return undefined;
    if (typeof window === "undefined" || !window.requestAnimationFrame) { setVal(target); return undefined; }
    let raf = 0; const start = performance.now();
    const tick = (now: number): void => {
      const t = Math.min(1, (now - start) / duration);
      setVal(target * (1 - Math.pow(1 - t, 3)));
      if (t < 1) raf = requestAnimationFrame(tick); else setVal(target);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, active, duration]);
  return val;
}

export function Home(): JSX.Element {
  const { data: me } = useMe();
  const leads = useCount("leads");
  const deals = useCount("deals");
  const quotes = useQuery<{ rows: Row[] }>({ queryKey: ["list", "quotes"], queryFn: () => api.get("/quotes") });
  const reminders = useQuery<{ rows: Row[] }>({ queryKey: ["reminders-today"], queryFn: () => api.get("/reminders/today") });

  const openDeals = deals.data?.rows ?? [];
  const pipelineValue = openDeals.reduce((acc, d) => acc + Number(d["amount"] ?? 0), 0);

  return (
    <div className="space-y-6 stagger">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight" style={{ background: "linear-gradient(90deg, var(--text), var(--primary))", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
            Hola, {me?.firstName}
          </h1>
          <p className="text-sm text-ink-secondary mt-0.5">Este es el resumen de tu actividad comercial.</p>
        </div>
        <ViewPortfolioButton />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi label="Leads" value={leads.data?.total ?? leads.data?.rows.length ?? 0} to="/leads" loading={leads.isLoading} icon={Target} tone="primary" />
        <Kpi label="Negocios" value={deals.data?.total ?? openDeals.length} to="/deals" loading={deals.isLoading} icon={Handshake} tone="success" />
        <Kpi label="Valor del pipeline" value={pipelineValue} to="/deals" loading={deals.isLoading} icon={TrendingUp} tone="warning" money />
        <Kpi label="Cotizaciones" value={quotes.data?.rows.length ?? 0} to="/quotes" loading={quotes.isLoading} icon={FileText} tone="accent" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="p-5">
          <h3 className="text-base font-semibold text-ink mb-3">Recordatorios de hoy</h3>
          {reminders.isLoading ? <Loading /> : (reminders.data?.rows ?? []).length === 0 ? (
            <p className="text-sm text-ink-muted">Sin recordatorios para hoy.</p>
          ) : (
            <ul className="space-y-2">
              {(reminders.data?.rows ?? []).map((r) => (
                <li key={String(r["id"])} className="text-sm text-ink-secondary flex items-center gap-2">
                  <Clock size={16} strokeWidth={1.75} className="text-warning shrink-0" /> {String(r["title"] ?? r["subject"] ?? "Recordatorio")}
                </li>
              ))}
            </ul>
          )}
        </Card>
        <Card className="p-5">
          <h3 className="text-base font-semibold text-ink mb-3">Accesos rápidos</h3>
          <div className="grid grid-cols-2 gap-2">
            {QUICK.map(([to, label, Icon]) => (
              <Link key={to} to={to} className="btn-secondary justify-start"><Icon size={16} strokeWidth={1.75} /> {label}</Link>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}

const QUICK: Array<[string, string, LucideIcon]> = [
  ["/leads", "Nuevo lead", Target],
  ["/deals", "Nuevo negocio", Handshake],
  ["/quotes", "Nueva cotización", FileText],
  ["/activities", "Nueva actividad", CalendarCheck],
];

const TONE: Record<string, { bg: string; fg: string }> = {
  primary: { bg: "bg-primary-soft", fg: "text-primary" },
  success: { bg: "bg-success-soft", fg: "text-success" },
  warning: { bg: "bg-warning-soft", fg: "text-warning" },
  accent: { bg: "bg-accent-soft", fg: "text-accent" },
};

function Kpi(
  { label, value, to, loading, icon: Icon, tone, money }:
  { label: string; value: number; to: string; loading: boolean; icon: LucideIcon; tone: string; money?: boolean },
): JSX.Element {
  const n = useCountUp(value, !loading);
  const shown = money ? formatMoney(String(Math.round(n))) : String(Math.round(n));
  const t = TONE[tone] ?? TONE["primary"]!;
  return (
    <Link to={to}>
      <Card className="p-5 transition-all duration-150 hover:shadow-md hover:-translate-y-0.5">
        <div className="flex items-center justify-between">
          <div className="text-[11px] text-ink-muted uppercase tracking-wider font-medium">{label}</div>
          <span className={`w-10 h-10 rounded-xl grid place-items-center ${t.bg} ${t.fg}`}>
            <Icon size={20} strokeWidth={1.75} />
          </span>
        </div>
        <div className="text-2xl font-bold text-ink mt-2 tnum">{loading ? "—" : shown}</div>
      </Card>
    </Link>
  );
}
